import { Button, message, Space, Steps } from "antd"
import { Formik, FormikProps } from "formik"
import React, { FC, useContext, useEffect, useState, useCallback, useMemo } from "react"
import { useHistory, useLocation } from "react-router"
import {
    BookerInput,
    BookerPaymentMethodInput,
    GetCompanyQuery,
    GetUpdatedBookerDocument,
    ThirdPartyLoginInput,
    ThirdPartyUserInput,
    UpdateBookerAccountEndUserAppInput,
    EndUserEmailRegistrationStatus,
    useEmailRegistrationStatusForEndUserAppLazyQuery,
    useRequestBookerLinkRegistrationMutation,
    useCreateAndRegisterThirdPartyUserAndBookerMutation,
    useCreateBookerMutation,
    useGetUpdatedBookerQuery,
    useUpdateBookerAccountEndUserAppMutation,
} from "../../generated/graphql"
import { createInputHelpers } from "../../helpers/createInputHelpers"
import { URL_LOGIN } from "../../models/url"
import LayoutForm from "../../views/LayoutForm/LayoutForm"
import LoginContainer from "../../views/LoginContainer/LoginContainer"
import { buildRegistrationPanelValidationSchema, registrationPanelValidationInitialValues, RegistrationPanelValidationType } from "./RegistrationPanelValidationSchema"
import { MainContext } from "../../layouts/Main/MainLayout"
import { omit } from "../../helpers/omit"
import { ObjectUtils } from "../../helpers/deepDiffMapper"
import RegistrationPanelStep0 from "./RegistrationPanelStep0"
import RegistrationPanelStep1 from "./RegistrationPanelStep1"
import RegistrationPanelStepButtons from "./RegistrationPanelStepButtons"
import RegistrationPanelErrors, { ErrorType } from "./RegistrationPanelErrors"
import { Link } from "react-router-dom"
import { LocationState } from "../../types"
import ErrorBoundary from "../../components/ErrorBoundary"
import { Maybe } from "graphql/jsutils/Maybe"
import { isEmailNotUniqueError, sanatizeErrors } from "./loginRegisterHelpers"
import { useTranslation } from "react-i18next"
import * as Sentry from "@sentry/react"
import { getGraphQLErrorMessage } from "../../helpers/sentry"
import { safeStorage } from "../../helpers/safeStorage"
import { clearPersistedLoginFromUrl, persistLoginFromUrl, resolveLoginFromUrl } from "./loginFromUrl"

export type FieldErrorsType = { [key in keyof Partial<BookerInput>]: any }

type RegistrationPanelProps = {
    useCase?: "registration" | "account"
    companySettings?: GetCompanyQuery["companyPublic"]
}

const RegistrationPanel: FC<RegistrationPanelProps> = ({ useCase = "registration", companySettings }) => {
    const history = useHistory()
    const location = useLocation<LocationState>()
    const { t, i18n } = useTranslation()
    // Priorität: Location-State > persistierter Wert (F5 im Formular verliert den State) > URL_BASE
    const fromUrl = resolveLoginFromUrl(location.state?.fromUrl)
    const { userAuthDate } = useContext(MainContext)

    // fromUrl beim Mount persistieren — ein Reload (F5) im Registrierungsformular
    // verwirft den Location-State; ohne Persistenz landete der User nach
    // erfolgreicher Registrierung auf der Startseite statt zurück in seiner Buchung.
    // NUR im Registrierungs-Flow: Im Konto-Kontext (useCase="account") transportiert
    // fromUrl bloß die interne Herkunft (z.B. /account/payment) — als loginFromUrl
    // persistiert würde sie einen späteren Login unerwartet dorthin umleiten.
    const stateFromUrlPathname = location.state?.fromUrl?.pathname
    useEffect(() => {
        if (useCase === "registration") persistLoginFromUrl(location.state?.fromUrl)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stateFromUrlPathname])

    const [errorType, setErrorType] = useState<keyof ErrorType>("errorNotFound")
    const [emailIsUnique, setEmailIsUnique] = useState(true)
    const [emailStatus, setEmailStatus] = useState<EndUserEmailRegistrationStatus | undefined>()
    const [confirmationMailState, setConfirmationMailState] = useState<"idle" | "loading" | "sent">("idle")
    const [step, setStep] = useState(useCase === "registration" ? 0 : 1)
    const [isLoading, setIsLoading] = useState(false)
    const [createAndRegister, { error: createAndRegisterError }] = useCreateAndRegisterThirdPartyUserAndBookerMutation()
    const [createBooker, { data: createBookerData, error: createBookerError, loading: createBookerLoading }] = useCreateBookerMutation({})
    const [updateBooker, { loading: updateBookerLoading }] = useUpdateBookerAccountEndUserAppMutation()

    const useCaseLabel = useCase === "registration" ? t("registration.useCaseLabel.registration", "Registrieren") : t("registration.useCaseLabel.account", "Account bearbeiten")
    const { Step } = Steps

    // HANDLE BOOKERDATA TO WORK WITH -------------------------------------------------------------------------------------------
    const loggedInUser = safeStorage.getItem("loggedInUser")
    const isLoggedIn = loggedInUser

    const [currentBookerData, setCurrentBookerData] = useState<RegistrationPanelValidationType | null>(() => {
        try {
            const stored = safeStorage.getItem("booker")
            if (!stored) return null
            const parsed = JSON.parse(stored)
            return parsed ?? null
        } catch {
            return null
        }
    })

    const { data: fetchedBookerData, loading: loadingFetchedBookerData } = useGetUpdatedBookerQuery({
        skip: !isLoggedIn || !currentBookerData?.id,
        variables: { id: currentBookerData?.id ?? "" },
        // pollInterval: 120000,
        fetchPolicy: "network-only",
    })

    useEffect(() => {
        if (fetchedBookerData?.booker) {
            const thirdPartyUserFields = ["username", "password"]
            const updatedBookerData = omit(thirdPartyUserFields, fetchedBookerData.booker)

            setCurrentBookerData(updatedBookerData)
        }
    }, [fetchedBookerData])

    const refetchBookerData = (formikProps: FormikProps<RegistrationPanelValidationType>) => {
        if (isLoggedIn && currentBookerData?.id) {
            const updatedBookerData = { ...fetchedBookerData?.booker, ...currentBookerData }
            if (updatedBookerData && typeof updatedBookerData === "object" && "id" in updatedBookerData) {
                setCurrentBookerData(updatedBookerData)
                formikProps.setValues(updatedBookerData)
            }
        }
    }

    useEffect(() => {
        if (currentBookerData && isLoggedIn) {
            safeStorage.setItem("booker", JSON.stringify(currentBookerData))
        }
    }, [currentBookerData, isLoggedIn])

    const updateCurrentBookerData = useCallback((values: RegistrationPanelValidationType | BookerInput | undefined | any, initiator?: string) => {
        if (!values) return

        setCurrentBookerData((prevData) => {
            if (!prevData) return values

            const updatedBookerData = { ...prevData, ...values }
            const diff = ObjectUtils.diff(prevData, updatedBookerData)

            Object.entries(diff.added).forEach(([key]) => {
                delete updatedBookerData[key]
            })

            return updatedBookerData
        })
    }, [])

    // Error Boundary hinzufügen
    const handleError = useCallback((error: any) => {
        console.error("Registration Error:", error)
        message.error("Es ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut.")
    }, [])

    // LOADING STATUS ------------------------------------------------------------------------------------------------------------
    useEffect(() => {
        if (createBookerLoading || updateBookerLoading) {
            setIsLoading(true)
        } else {
            setIsLoading(false)
        }
    }, [createBookerLoading, updateBookerLoading])

    // EMAIL UNIQUESS ------------------------------------------------------------------------------------------------------------
    // Dreiwertige Status-Query statt des alten Booleans (sc-3358): CONFIRMABLE =
    // Booker existiert, hat aber keinen eigenen Login → Bestätigungs-Flow anbieten.
    const [checkUniquess, { data: statusData, loading: checkUniquenessLoading, error: checkUniquessError }] = useEmailRegistrationStatusForEndUserAppLazyQuery({ fetchPolicy: "network-only" })

    useEffect(() => {
        const status = statusData?.emailRegistrationStatusForEndUserApp
        if (status !== undefined) {
            setEmailStatus(status)
            setEmailIsUnique(status === EndUserEmailRegistrationStatus.Available)
            // Neue Prüfung (andere E-Mail) → einen früheren Mail-Versand-Zustand zurücksetzen
            setConfirmationMailState("idle")
        }
    }, [checkUniquenessLoading, checkUniquessError, statusData])

    const [requestBookerLink] = useRequestBookerLinkRegistrationMutation()

    const requestConfirmationMail = async (email: string) => {
        setConfirmationMailState("loading")
        try {
            await requestBookerLink({ variables: { email } })
            setConfirmationMailState("sent")
        } catch (error: any) {
            setConfirmationMailState("idle")
            message.error(t("registration.confirmationMailError", "Die Bestätigungs-E-Mail konnte nicht angefordert werden. Bitte versuche es erneut."), 3)
        }
    }

    // METHODS ------------------------------------------------------------------------------------------------------------

    const nextStep = (errors: FieldErrorsType, thirdPartyUserFields: Array<keyof ThirdPartyLoginInput | "passwordConfirmation">, formikProps: FormikProps<RegistrationPanelValidationType>) => {
        const thirdPartyErrorFields: Array<any> = []

        Object.keys(errors).forEach((key) => {
            //@ts-ignore
            if (thirdPartyUserFields.includes(key)) {
                thirdPartyErrorFields.push(key)
            }
        })

        if (!thirdPartyErrorFields.length) {
            setStep(1)
        }
    }

    const createOrUpdate = async (formikProps: FormikProps<RegistrationPanelValidationType>) => {
        const { hasErrors } = sanatizeErrors(formikProps)

        if (!hasErrors) {
            const { id, email, password, username } = formikProps.values

            const formikValues = formikProps.values

            const thirdPartyUserInput: ThirdPartyUserInput = {
                username: username,
                email: email,
                password: password,
            }

            const newBookerData: BookerInput = {
                id: formikValues?.id,
                customer: formikValues?.customer,
                gender: formikValues?.gender,
                firstname: formikValues?.firstname,
                lastname: formikValues?.lastname,
                addition: formikValues?.addition,
                street: formikValues?.street,
                houseNumber: formikValues?.houseNumber,
                postCode: formikValues?.postCode,
                city: formikValues?.city,
                phoneNumber: formikValues?.phoneNumber,
                mobileNumber: formikValues?.mobileNumber,
                email: formikValues?.email,
                initialContact: formikValues?.initialContact,
                birthday: formikValues?.birthday,
                dsgvo: formikValues?.dsgvo,
                newsletter: formikValues?.newsletter,
                healthInsuranceFund: formikValues?.healthInsuranceFund,
                annotation: formikValues?.annotation,
                nonDisclosureNotice: formikValues?.nonDisclosureNotice,
                credit: formikValues?.credit,
                // paymentMethods: formikValues?.paymentMethods,
                // Leere Collections als undefined senden statt [] — das Backend
                // wuerde sonst TypeORM-Cascade auf den existierenden Booker triggern und
                // saemtliche Attendees/PaymentMethods entkoppeln (bookerId = NULL).
                // Backend-Schutz existiert seit fix(booker) 4f2583d80 zusaetzlich.
                attendees: formikValues?.attendees?.length ? formikValues.attendees : undefined,
                createdBy: formikValues?.createdBy,
                paymentMethods: formikValues?.paymentMethods?.length ? formikValues.paymentMethods : undefined,
            }

            if (isLoggedIn) {
                if (id === undefined) {
                    try {
                        await createBooker({
                            variables: { newBookerData },
                        })
                            .then(() => updateCurrentBookerData(createBookerData?.createBooker, "createBooker"))
                            .then(message.success("Bucher wurde erfolgreich angelegt", 3))
                    } catch (error: any) {
                        console.group("ERROR createBooker")
                        console.log("ERROR: ", error)
                        console.groupEnd()
                    } finally {
                        formikProps.setSubmitting(false)
                    }
                } else {
                    try {
                        // console.log("1.2 updateBooker()")
                        const rawUpdateBookerData = omit(["password", "passwordConfirmation", "createdBy"], {
                            ...newBookerData,
                            ...formikProps.values,
                        })
                        const booker = rawUpdateBookerData
                        const updateBookerEndUserAppData: UpdateBookerAccountEndUserAppInput = {
                            id: booker?.id ?? "",
                            customer: { id: booker?.customerId },
                            gender: booker?.gender,
                            firstname: booker?.firstname,
                            lastname: booker?.lastname,
                            street: booker?.street,
                            houseNumber: booker?.houseNumber,
                            postCode: booker?.postCode,
                            city: booker?.city,
                            phoneNumber: booker?.phoneNumber,
                            mobileNumber: booker?.mobileNumber,
                            birthday: booker?.birthday,
                            dsgvo: booker?.dsgvo,
                            // Leere paymentMethods als undefined senden statt [] —
                            // sonst TypeORM-Orphan-Cascade (siehe Backend-Fix 4f2583d80).
                            paymentMethods: booker?.paymentMethods?.length ? booker.paymentMethods : undefined,
                        }

                        try {
                            await updateBooker({
                                variables: {
                                    updateBookerEndUserAppData,
                                    fromEndUserApp: true,
                                },
                                refetchQueries: ["GetUpdatedBooker", { query: GetUpdatedBookerDocument, variables: { id: booker?.id } }],
                            })
                                .then((res) => {
                                    const updatedBookerResult = res?.data?.updateBookerAccountEndUserApp
                                    if (updatedBookerResult) {
                                        setCurrentBookerData(formikProps.values)
                                    }
                                })
                                // .then(() => message.success("Daten erfolgreich gespeichert.", 3))
                                .then(() => message.success({ content: "Daten erfolgreich gespeichert." }, 3))
                                .then(() => formikProps.setSubmitting(false))
                        } catch (error: any) {
                            message.error("Es gab eine Fehler beim Speichern der Daten.", 3)
                        }
                    } catch (error: any) {
                        console.group("ERROR updateBooker")
                        console.log("ERROR: ", error)
                        console.groupEnd()
                        message.error("Es gab eine Fehler beim Speichern der Daten.", 3).then(() => formikProps.setSubmitting(false))
                    } finally {
                        // formikProps.setSubmitting(false)
                    }
                }
            } else {
                try {
                    const { data } = await createAndRegister({
                        variables: {
                            newBookingData: newBookerData,
                            thirdPartyUserInput,
                        },
                    })

                    const registeredUser = await data?.createAndRegisterThirdPartyUserAndBooker

                    if (registeredUser?.accessToken && registeredUser?.refreshToken) {
                        updateCurrentBookerData(registeredUser.booker, "createAndRegisterThirdPartyUserAndBooker")
                        safeStorage.setItem("accessToken", registeredUser.accessToken)
                        safeStorage.setItem("refreshToken", registeredUser.refreshToken)
                        safeStorage.setItem("booker", JSON.stringify(registeredUser.booker))
                        safeStorage.setItem("loggedInUser", registeredUser.id)
                        if (userAuthDate) {
                            userAuthDate(new Date())
                        }
                        message.success("Account wurde erfolgreich angelegt.", 3)
                        // Persistierte fromUrl ist verbraucht — entfernen, damit kein
                        // späterer Login an einem veralteten Ziel landet.
                        clearPersistedLoginFromUrl()
                        history.push(fromUrl)
                    }
                } catch (error: any) {
                    console.error("error createAndRegisterUserAndBooker: ", error)
                    const rawErrorMessage = getGraphQLErrorMessage(error) || ""
                    // Strukturierte Erkennung (extensions/networkError) mit String-Matching als Fallback
                    if (isEmailNotUniqueError(error, rawErrorMessage)) {
                        setErrorType("emailIsNotUnique")
                    } else {
                        Sentry.captureException(error)
                    }
                } finally {
                    formikProps.setSubmitting(false)
                }
            }
        }
    }

    const getInitialValues = (): RegistrationPanelValidationType => {
        if (useCase === "registration") {
            return registrationPanelValidationInitialValues
        }

        if (currentBookerData && typeof currentBookerData === "object" && "id" in currentBookerData) {
            return currentBookerData
        }

        return registrationPanelValidationInitialValues
    }

    const validationSchema = useMemo(() => buildRegistrationPanelValidationSchema(t, useCase), [t, i18n.language, useCase])

    const content = (
        <Formik<RegistrationPanelValidationType>
            enableReinitialize
            // validateOnBlur
            initialValues={getInitialValues()}
            // validationSchema={useCase === "account" ? BookerInputValidationSchema : RegistrationPanelValidationSchema}
            validationSchema={validationSchema}
            validateOnMount={useCase === "account" ? true : false}
            // validateOnMount
            onSubmit={async () => {
                // Submit läuft über createOrUpdate in den Step-Buttons.
            }}
        >
            {(formikProps) => {
                const { handleSubmit, setFieldValue } = formikProps
                const { handleBlur, validateStatus } = createInputHelpers(formikProps)
                const bookerDataFields: Array<keyof BookerInput> = ["gender", "birthday", "firstname", "lastname", "street", "postCode", "city", "mobileNumber", "dsgvo"]
                const noBookerDataFieldIsTouched = bookerDataFields.every((field) => !formikProps.touched[field])
                const thirdPartyUserFields: Array<keyof ThirdPartyLoginInput | "passwordConfirmation"> = ["username", "password", "passwordConfirmation"]

                const checkIfExists = (field: string, value: string, shouldValidate: boolean) => {
                    setFieldValue("email", value, false)
                    handleBlur("username")
                    checkUniquess({ variables: { email: value } })
                    setFieldValue(field, value, shouldValidate)
                }

                const buttonsProps = {
                    step,
                    formikProps,
                    nextStep,
                    thirdPartyUserFields,
                    bookerDataFields,
                    emailIsUnique,
                    emailTakenByStaff: emailStatus === EndUserEmailRegistrationStatus.TakenByStaff,
                    noBookerDataFieldIsTouched,
                    isLoading,
                    createOrUpdate,
                    useCase,
                    useCaseLabel,
                    loadingFetchedBookerData,
                    refetchBookerData,
                }
                // console.log("formikProps.values: ", formikProps.values)

                return (
                    <LayoutForm onSubmitCapture={handleSubmit} columns={1}>
                        {useCase === "registration" && (
                            <Steps style={{ padding: "0 10px" }} size='small' current={step}>
                                <Step
                                    title={<span style={{ cursor: "pointer" }}>{t("registration.account", "Account")}</span>}
                                    status={validateStatus(thirdPartyUserFields)}
                                    onClick={() => setStep(0)}
                                />
                                <Step title={t("registration.contactData", "Kontaktdaten")} status={validateStatus(bookerDataFields)} />
                            </Steps>
                        )}
                        <RegistrationPanelErrors
                            errorType={errorType}
                            createBookerError={createBookerError}
                            createAndRegisterError={createAndRegisterError}
                            emailIsUnique={emailIsUnique}
                            emailStatus={emailStatus}
                            confirmationMailState={confirmationMailState}
                            onRequestConfirmationMail={() => requestConfirmationMail(formikProps.values.email ?? "")}
                        />
                        {step === 0 && <RegistrationPanelStep0 formikProps={formikProps} checkIfExists={checkIfExists} checkUniquess={checkUniquess} thirdPartyUserFields={thirdPartyUserFields} />}
                        {step === 1 && <RegistrationPanelStep1 formikProps={formikProps} checkIfExists={checkIfExists} companySettings={companySettings} useCase={useCase} />}
                        <RegistrationPanelStepButtons registrationPanelStepButtonsProps={buttonsProps} />
                    </LayoutForm>
                )
            }}
        </Formik>
    )

    const Footer = () => {
        if (useCase === "registration") {
            return (
                <Space direction='vertical' style={{ margin: `0 20px`, width: "calc(100% - 40px)" }}>
                    <div>{t("registration.alreadyRegistered", "Bereits einen Account?")}</div>
                    <Link
                        to={{
                            pathname: URL_LOGIN,
                            state: { fromUrl },
                        }}
                    >
                        <Button block type='primary' ghost>
                            {t("registration.login", "Jetzt einloggen")}
                        </Button>
                    </Link>
                </Space>
            )
        }

        return null
    }

    // Im Konto-Kontext liefert die Account-Shell (SubHeader) Titel + Layout —
    // die zentrierte Login-Karte mit Logo/h1/"Zur Kurssuche" entfällt.
    if (useCase === "account") {
        return <ErrorBoundary>{content}</ErrorBoundary>
    }

    return (
        <ErrorBoundary>
            <LoginContainer title={useCaseLabel} size={step === 0 ? "xs" : "s"} content={content} actions={[<Footer />]} />
        </ErrorBoundary>
    )
}

export default RegistrationPanel
